/* =============================================================
   AuthProvider — single source of truth for the current session.
   Subscribes to Supabase auth-state changes so the entire app
   re-renders cleanly when the user signs in / signs out / refreshes.
   ============================================================ */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { migrateLocalToServer } from './psychLocal';

interface AuthContextValue {
  /** null while we're loading the first session; { session: null } after if signed out. */
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthContextValue>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Initial session load (may be cached in localStorage by Supabase SDK)
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });

    // Live updates: sign-in, sign-out, token refresh, OAuth callback
    const { data: sub } = supabase.auth.onAuthStateChange((evt, s) => {
      if (!mounted) return;
      setSession(s);

      // On a fresh sign-in, migrate any anonymous Psych Safety state into
      // the user's DB rows. Best-effort: failure here doesn't block UX.
      if (evt === 'SIGNED_IN' && s?.user?.id) {
        migrateLocalToServer(s.user.id).catch((e) => {
          console.warn('[auth] Psych Safety local->server migration failed:', e);
        });
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    loading,
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(Ctx);
}

/** Convenience guard: redirect if not authed. Use in protected pages. */
export function useRequireAuth(redirectTo = '/signin') {
  const auth = useAuth();
  useEffect(() => {
    if (!auth.loading && !auth.user) {
      window.location.replace(redirectTo);
    }
  }, [auth.loading, auth.user, redirectTo]);
  return auth;
}

/**
 * Auth + approval guard. Bounces unauthed users to /signin and unapproved
 * users to /dashboard (which renders the "pending approval" panel). Use
 * this on every CYBHI workspace/checklist page. Returns the auth value;
 * `approved` is undefined while the RPC is pending.
 */
export function useRequireApproved() {
  const auth = useRequireAuth();
  const [approved, setApproved] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (!auth.user) {
      setApproved(undefined);
      return;
    }
    let cancelled = false;
    supabase.rpc('am_i_approved').then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        // If the RPC isn't present (e.g. migration 005 not applied yet),
        // fall back to "allow" so the app keeps working in dev.
        console.warn('[auth] am_i_approved failed:', error.message);
        setApproved(true);
        return;
      }
      setApproved(!!data);
    });
    return () => {
      cancelled = true;
    };
  }, [auth.user]);

  useEffect(() => {
    if (auth.loading) return;
    if (!auth.user) return;
    if (approved === false) {
      window.location.replace('/dashboard');
    }
  }, [auth.loading, auth.user, approved]);

  return { ...auth, approved };
}
